import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tooltip } from '@mantine/core';
import { CaretDown, GoogleLogo, MetaLogo } from '@phosphor-icons/react';
import api from '../utils/api';
import { getGoogleAccounts } from '../utils/googleAccounts';
import { useAuth } from '../hooks/useAuth';
import SelectAccountModal from './SelectAccountModal';

const ALL = '__all__';

/**
 * Per-platform account switcher (top bar). One for Google, one for Meta. The
 * selected account scopes the whole app. Disabled with a tooltip when no account
 * of that platform is connected.
 */
export default function AccountSwitcher({ platform }) {
  const { t } = useTranslation();
  const isMeta = platform === 'meta_ads';
  const platformLabel = isMeta ? 'Meta Ads' : 'Google Ads';
  const Logo = isMeta ? MetaLogo : GoogleLogo;

  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState(null); // null = loading
  const [currentId, setCurrentId] = useState('');

  // Re-fetch whenever the connection state changes (connect/disconnect/switch in
  // Settings calls refreshUser, which updates these), so the switcher stays in
  // sync without a browser refresh.
  const metaCount = (user?.organization?.platform_accounts || []).filter(p => p.platform === 'meta_ads' && p.is_active).length;
  const authSignal = `${user?.google_ads_customer_id || ''}|${user?.google_ads_connected ? 1 : 0}|${metaCount}`;

  useEffect(() => {
    getGoogleAccounts()
      .then(res => {
        setAccounts((res.data.accounts || []).filter(a => (a.platform || 'google_ads') === platform));
        setCurrentId((isMeta ? res.data.current_meta_account_id : res.data.current_google_account_id) || '');
      })
      .catch(() => setAccounts([]));
  }, [platform, isMeta, authSignal]);

  // While loading, render a placeholder of the same size so the top bar never
  // jumps; the real state replaces it in place when the request finishes.
  if (accounts === null) {
    return (
      <Button
        variant="default"
        disabled
        leftSection={<Logo size={15} weight="bold" />}
        rightSection={<CaretDown size={12} />}
        styles={{
          root: { height: 36, width: 210, paddingLeft: 10, paddingRight: 8 },
          inner: { justifyContent: 'flex-start' },
          label: { flex: 1, overflow: 'hidden' },
          section: { marginLeft: 0, marginRight: 6 },
        }}
      >
        <div style={{ textAlign: 'left', lineHeight: 1.15, overflow: 'hidden', width: '100%' }}>
          <div style={{ fontSize: 12, fontWeight: 600 }}>{platformLabel}</div>
          <div style={{ fontSize: 10, color: 'var(--mantine-color-gray-5)' }}>{t('shell.accountSwitcher.loading')}</div>
        </div>
      </Button>
    );
  }

  const connected = accounts.length > 0;
  const selectedAccount = accounts.find(a => a.id === currentId);
  const isAll = currentId === ALL;
  const multiIds = currentId.includes(',') ? currentId.split(',').filter(Boolean) : null;
  const brand = isMeta ? 'Meta' : 'Google';
  const name = isAll ? t('shell.accountSwitcher.allAccounts', { brand })
    : multiIds ? t('shell.accountSwitcher.multiAccounts', { count: multiIds.length, brand })
    : (selectedAccount?.custom_name || selectedAccount?.name || platformLabel);
  const subId = isAll ? t('shell.accountSwitcher.accountsCount', { count: accounts.length })
    : multiIds ? multiIds.join(', ')
    : (selectedAccount?.id || '');

  const btn = (
    <Button
      variant="default"
      disabled={!connected}
      onClick={() => connected && setOpen(true)}
      leftSection={<Logo size={15} weight="bold" color={isMeta && connected ? '#0866FF' : undefined} />}
      rightSection={<CaretDown size={12} />}
      styles={{
        root: { height: 36, width: 210, paddingLeft: 10, paddingRight: 8 },
        inner: { justifyContent: 'flex-start' },
        label: { flex: 1, overflow: 'hidden' },
        section: { marginLeft: 0, marginRight: 6 },
      }}
    >
      <div style={{ textAlign: 'left', lineHeight: 1.15, overflow: 'hidden', width: '100%' }}>
        <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {connected ? name : platformLabel}
        </div>
        <div style={{ fontSize: 10, color: 'var(--mantine-color-gray-5)', fontFamily: 'var(--mantine-font-family-monospace)' }}>
          {connected ? subId : t('shell.accountSwitcher.notConnected')}
        </div>
      </div>
    </Button>
  );

  return (
    <>
      <Tooltip
        label={connected
          ? (accounts.length > 1 || isAll ? t('shell.accountSwitcher.switchTooltip', { platform: platformLabel }) : t('shell.accountSwitcher.accountTooltip', { platform: platformLabel }))
          : t('shell.accountSwitcher.connectTooltip', { platform: platformLabel })}
        withArrow
      >
        {/* span wrapper so the tooltip works on a disabled button */}
        <span style={{ display: 'inline-flex' }}>{btn}</span>
      </Tooltip>
      {open && (
        <SelectAccountModal
          platform={platform}
          onClose={() => setOpen(false)}
          onSelected={() => { window.location.reload(); }}
        />
      )}
    </>
  );
}
